// src/pages/admin/UserManagement.jsx
// Admin page for managing users at scale

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserCog, Search, AlertTriangle, Check, X,
  ChevronLeft, ChevronRight, Download, RefreshCw, Eye, Shield, Lock, UserX, EyeOff
} from "lucide-react";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const ROLES = [
  { value: "agent", label: "Agent", color: "bg-blue-100 text-blue-800" },
  { value: "agency_admin", label: "Agency Admin", color: "bg-teal-100 text-teal-800" },
  { value: "agency_owner", label: "Agency Owner", color: "bg-emerald-100 text-emerald-800" },
  { value: "fmo", label: "FMO", color: "bg-amber-100 text-amber-800" },
  { value: "founder", label: "Founder", color: "bg-purple-100 text-purple-800" },
];

const ITEMS_PER_PAGE = 25;

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Search and filters
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Stats
  const [stats, setStats] = useState(null);

  // Success message
  const [successMessage, setSuccessMessage] = useState(null);

  // Deactivate confirmation
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  // Fetch current user to check permissions
  useEffect(() => {
    async function fetchCurrentUser() {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
        const data = await res.json();
        if (data.ok && data.user) {
          setCurrentUser(data.user);
          if (!["admin", "agency_admin", "agency_owner", "fmo", "founder"].includes(data.user.role)) {
            navigate("/dashboard");
          }
        } else {
          navigate("/login");
        }
      } catch (err) {
        navigate("/login");
      }
    }
    fetchCurrentUser();
  }, [navigate]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = showDeactivated
        ? `${API_BASE}/admin/users?includeDeactivated=true`
        : `${API_BASE}/admin/users`;
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();
      if (data.ok) {
        setUsers(data.users || []);
        setStats(data.stats || null);
      } else {
        setError(data.error || "Failed to load users");
      }
    } catch (err) {
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  }, [showDeactivated]);

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [currentUser, fetchUsers]);

  // Filter and sort users
  useEffect(() => {
    let result = [...users];

    // Filter deactivated users (hide by default)
    if (!showDeactivated) {
      result = result.filter((user) => user.is_active !== false && !user.deactivated_at);
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (user) =>
          user.email?.toLowerCase().includes(searchLower) ||
          user.name?.toLowerCase().includes(searchLower)
      );
    }

    // Role filter
    if (roleFilter !== "all") {
      result = result.filter((user) => (user.role || "agent") === roleFilter);
    }

    // Status filter
    if (statusFilter === "locked") {
      result = result.filter((user) => user.lockout_until && new Date(user.lockout_until) > new Date());
    } else if (statusFilter === "mfa") {
      result = result.filter((user) => user.mfa_enabled);
    } else if (statusFilter === "active") {
      result = result.filter((user) => !user.lockout_until || new Date(user.lockout_until) <= new Date());
    } else if (statusFilter === "deactivated") {
      result = result.filter((user) => user.is_active === false || user.deactivated_at);
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortBy] || "";
      let bVal = b[sortBy] || "";
      if (sortBy === "created_at" || sortBy === "last_login_at") {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    setFilteredUsers(result);
    setTotalPages(Math.ceil(result.length / ITEMS_PER_PAGE) || 1);
    setCurrentPage(1);
  }, [users, search, roleFilter, statusFilter, showDeactivated, sortBy, sortOrder]);

  // Get paginated users
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Export users to CSV
  function exportUsers() {
    const headers = ["Email", "Role", "MFA", "Created", "Last Login"];
    const rows = filteredUsers.map((u) => [
      u.email,
      u.role || "agent",
      u.mfa_enabled ? "Yes" : "No",
      u.created_at ? new Date(u.created_at).toLocaleDateString() : "",
      u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "Never",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  }

  // Deactivate user (HIPAA-compliant - preserves audit logs)
  async function deactivateUser(user) {
    if (user.id === currentUser?.id) {
      setError("Cannot deactivate your own account");
      return;
    }

    setDeactivating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${user.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (data.ok) {
        setSuccessMessage(`User ${user.email} has been deactivated. Their email is now available for re-registration.`);
        setDeactivateConfirm(null);
        // Remove from local state
        setUsers(users.filter((u) => u.id !== user.id));
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(data.error || "Failed to deactivate user");
      }
    } catch (err) {
      setError("Failed to deactivate user");
    } finally {
      setDeactivating(false);
    }
  }

  if (loading && !users.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <UserCog className="w-8 h-8 text-amber-500" />
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          </div>
          <p className="text-gray-600">
            {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
            {search || roleFilter !== "all" || statusFilter !== "all" ? " (filtered)" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowDeactivated(!showDeactivated)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              showDeactivated
                ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {showDeactivated ? <Eye size={18} /> : <EyeOff size={18} />}
            {showDeactivated ? "Showing Deactivated" : "Show Deactivated"}
            {stats?.deactivatedUsers > 0 && !showDeactivated && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                {stats.deactivatedUsers}
              </span>
            )}
          </button>
          <button
            onClick={exportUsers}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium"
          >
            <Download size={18} />
            Export
          </button>
          <button
            onClick={fetchUsers}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="text-2xl font-bold text-blue-800">{stats.totalUsers || users.length}</div>
            <div className="text-sm text-blue-600">Total Users</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4">
            <div className="text-2xl font-bold text-green-800">{stats.activeToday || 0}</div>
            <div className="text-sm text-green-600">Active Today</div>
          </div>
          <div className="bg-amber-50 rounded-xl p-4">
            <div className="text-2xl font-bold text-amber-800">{stats.mfaEnabled || 0}</div>
            <div className="text-sm text-amber-600">MFA Enabled</div>
          </div>
          <div className="bg-red-50 rounded-xl p-4">
            <div className="text-2xl font-bold text-red-800">{stats.locked || 0}</div>
            <div className="text-sm text-red-600">Locked Accounts</div>
          </div>
        </div>
      )}

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
          <Check className="w-5 h-5" />
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
          <AlertTriangle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">All Roles</option>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="locked">Locked</option>
            <option value="mfa">MFA Enabled</option>
          </select>

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split("-");
              setSortBy(field);
              setSortOrder(order);
            }}
            className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500"
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="email-asc">Email A-Z</option>
            <option value="email-desc">Email Z-A</option>
            <option value="last_login_at-desc">Recent Login</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">User</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Role</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600 hidden md:table-cell">Status</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600 hidden lg:table-cell">Last Login</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    No users found
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => {
                  const isLocked = user.lockout_until && new Date(user.lockout_until) > new Date();
                  const isDeactivated = user.is_deactivated || user.is_active === false;
                  return (
                    <tr
                      key={user.id}
                      className={`cursor-pointer ${isDeactivated ? "bg-gray-50 opacity-60" : "hover:bg-gray-50"}`}
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            isDeactivated
                              ? "bg-gray-400"
                              : "bg-gradient-to-br from-amber-400 to-amber-600"
                          }`}>
                            {(user.email?.[0] || "?").toUpperCase()}
                          </div>
                          <div>
                            <div className={`font-medium truncate max-w-[200px] ${isDeactivated ? "text-gray-500" : "text-gray-900"}`}>
                              {user.email}
                              {isDeactivated && <span className="ml-2 text-xs text-red-500">(Deactivated)</span>}
                            </div>
                            {user.name && <div className="text-sm text-gray-500 truncate max-w-[200px]">{user.name}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-bold rounded ${ROLES.find((r) => r.value === (user.role || "agent"))?.color || "bg-gray-100 text-gray-800"}`}>
                          {user.role || "agent"}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {isDeactivated ? (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            <UserX size={14} /> Deactivated
                          </span>
                        ) : isLocked ? (
                          <span className="flex items-center gap-1 text-orange-600 text-sm">
                            <Lock size={14} /> Locked
                          </span>
                        ) : user.mfa_enabled ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <Shield size={14} /> MFA
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-sm text-gray-500">
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/users/${user.id}`);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            title="View Details"
                          >
                            <Eye size={18} className="text-gray-500" />
                          </button>
                          {user.id !== currentUser?.id && !isDeactivated && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeactivateConfirm(user);
                              }}
                              className="p-2 hover:bg-red-50 rounded-lg"
                              title="Deactivate User"
                            >
                              <UserX size={18} className="text-red-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <div className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Deactivate Confirmation Modal */}
      {deactivateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <UserX className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Deactivate User</h3>
                <p className="text-sm text-gray-500">HIPAA compliant - audit logs preserved</p>
              </div>
            </div>

            <p className="text-gray-700 mb-4">
              Are you sure you want to deactivate <strong>{deactivateConfirm.email}</strong>?
              This will disable their login and free up their email for re-registration.
              Audit logs will be preserved for compliance.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeactivateConfirm(null)}
                disabled={deactivating}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deactivateUser(deactivateConfirm)}
                disabled={deactivating}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deactivating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Deactivating...
                  </>
                ) : (
                  <>
                    <UserX size={18} />
                    Deactivate User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
